import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, Search } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { Flotante } from './ui';
import { normalizarTexto } from '../../utils/texto';

const SITUACIONES = {
    egresado: { texto: 'Egresado, sigue en el aparato', clase: 'text-rose-600' },
    no_existe: { texto: 'No está en Centro SURA', clase: 'text-amber-600' },
    activo: { texto: 'Activo', clase: 'text-slate-500' },
};

// Panel lateral con quién está cargado en un aparato (según su última lectura)
const UsuariosAparato = ({ aparato, onCerrar }) => {
    const [usuarios, setUsuarios] = useState(null);
    const [error, setError] = useState(null);
    const [texto, setTexto] = useState('');
    const cerrarRef = useRef(null);

    useEffect(() => {
        axiosInstance.get(`/api/asistencia/aparatos/${aparato.id}/usuarios`)
            .then(({ data }) => setUsuarios(data.usuarios))
            .catch(() => setError('No se pudo cargar la lista de usuarios del aparato.'));
    }, [aparato.id]);

    // onCerrar cambia en cada refresco de la pantalla: se lee por ref para no
    // devolver el foco al botón de cerrar cada 5 segundos
    const onCerrarRef = useRef(onCerrar);
    onCerrarRef.current = onCerrar;
    useEffect(() => {
        cerrarRef.current?.focus();
        const alTeclear = (e) => e.key === 'Escape' && onCerrarRef.current();
        window.addEventListener('keydown', alTeclear);
        return () => window.removeEventListener('keydown', alTeclear);
    }, []);

    const conteo = useMemo(() => {
        const c = { egresado: 0, no_existe: 0, activo: 0 };
        (usuarios || []).forEach((u) => { c[u.situacion] += 1; });
        return c;
    }, [usuarios]);

    const visibles = useMemo(() => {
        const q = normalizarTexto(texto.trim());
        if (!q) return usuarios || [];
        return (usuarios || []).filter((u) =>
            u.enroll_id.includes(q)
            || normalizarTexto(u.empleado?.nombre || '').includes(q)
            || normalizarTexto(u.nombre_aparato || '').includes(q));
    }, [usuarios, texto]);

    return (
        <Flotante>
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="usuarios-aparato-titulo">
            <div className="absolute inset-0 bg-slate-900/30" onClick={onCerrar} />
            <div className="relative w-full max-w-lg h-full bg-white shadow-xl flex flex-col">
                <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <h2 id="usuarios-aparato-titulo" className="text-lg font-semibold text-slate-900">
                            Usuarios de {aparato.nombre}
                        </h2>
                        {usuarios && (
                            <p className="text-sm text-slate-500 mt-0.5">
                                {usuarios.length} cargados
                                {conteo.egresado > 0 && <>, <span className="text-rose-600">{conteo.egresado} egresados</span></>}
                                {conteo.no_existe > 0 && <>, <span className="text-amber-600">{conteo.no_existe} que no están en Centro SURA</span></>}
                            </p>
                        )}
                    </div>
                    <button
                        ref={cerrarRef} onClick={onCerrar} aria-label="Cerrar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-slate-100">
                    <div className="relative">
                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="search" value={texto} onChange={(e) => setTexto(e.target.value)}
                            placeholder="Nombre o cédula"
                            className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {error && <p className="p-5 text-sm text-rose-600">{error}</p>}
                    {!usuarios && !error && (
                        <div className="flex justify-center p-10"><Loader2 size={24} className="animate-spin text-slate-400" /></div>
                    )}
                    {usuarios && visibles.length === 0 && (
                        <p className="p-5 text-sm text-slate-500">Nadie coincide con la búsqueda.</p>
                    )}
                    <ul className="divide-y divide-slate-50">
                        {visibles.map((u) => (
                            <li key={u.enroll_id} className="px-5 py-3">
                                <div className="flex items-baseline justify-between gap-3">
                                    <p className="font-medium text-slate-900 truncate">
                                        {u.empleado?.nombre || u.nombre_aparato || 'Sin nombre'}
                                    </p>
                                    <span className="text-xs text-slate-500 whitespace-nowrap">{u.credenciales.join(', ')}</span>
                                </div>
                                <p className="text-xs mt-0.5">
                                    <span className="text-slate-400 tabular-nums">C.I. {u.enroll_id}</span>
                                    {' '}<span className={SITUACIONES[u.situacion].clase}>{SITUACIONES[u.situacion].texto}</span>
                                    {u.admin && <span className="text-slate-500">, administrador del aparato</span>}
                                </p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
        </Flotante>
    );
};

export default UsuariosAparato;
